import TertiaryButton from '@components/system/ButtonField/TertiaryButton';
import CurrencyFormatter from '@components/system/CurrencyFormatter/CurrencyFormatter';
import {FormField} from '@components/system/FormField/FormField';
import GridTable, {
  DEFAULT_GRID_TABLE__OPTIONS
} from '@components/system/GridTable/GridTable';
import {CSSProperties} from '@material-ui/core/styles/withStyles';
import {
  IClaimsCPTData,
  IClaimsTicketDetails
} from '@shared/services/service-mappers/WorkflowMapper';
import {MTableToolbar} from 'material-table';
import React, {useEffect, useState} from 'react';
import {UseFormMethods} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {
  APP,
  EDITABLE_GRID_BORDER_COLOR,
  FONT_PRIMARY_COLOR,
  MESSAGE_GRID_HEADER_BACKGROUND_COLOR
} from 'src/style/variable';
import './ClaimsCPTFields.scss';
import {Button, IconButton, Tooltip} from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/DeleteOutline';
import CheckIcon from '@material-ui/icons/Check';
import CCMShowFullNotesDialog from '../../../ChronicCareManagement/module-components/CCMShowFullNotesDialog/CCMShowFullNotesDialog';

interface IClaimsCPTFieldsProps {
  methods: UseFormMethods;
  onClose: () => void;
  resetCPTCodesFieldValue: Partial<IClaimsCPTData>[];
  renderClaimsFields: (
    defaultFormValues: Partial<IClaimsTicketDetails['claims_data']>
  ) => JSX.Element;
}

type IClaimsCPTField = Partial<IClaimsCPTData> & {
  rowIdx: number;
  deleted?: boolean;
};

const ClaimsCPTFields = (props: IClaimsCPTFieldsProps): JSX.Element => {
  const {
    methods,
    onClose,
    resetCPTCodesFieldValue = [],
    renderClaimsFields
  } = props;
  const {t} = useTranslation();
  const [stateValues, setState] = useState<any>({
    moreDetails: false,
    cptCode: ''
  });
  const { moreDetails, cptCode } = stateValues;
  const currentCPTCodeData: IClaimsCPTField[] = methods.watch('cpt_data');

  const [cptData, setCPTData] = useState<IClaimsCPTField[]>(
    (currentCPTCodeData ?? []).map((i, idx) => {
      return {...i, rowIdx: idx + 1};
    })
  );

  const resetCPTCodes = () => {
    if (Array.isArray(resetCPTCodesFieldValue)) {
      setCPTData(
        resetCPTCodesFieldValue.map((i, idx) => {
          return {...i, rowIdx: idx + 1};
        })
      );
    } else {
      setCPTData([]);
    }
  };

  const [aggregateValues, setAggregateValues] = useState<
    IClaimsCPTData & {rowIdx: number}
  >({
    rowIdx: -1,
    allowed_amount: 0,
    balance_amount: 0,
    charges: 0,
    cpt_code: 0,
    insurance_amount: 0,
    paid_insurance_amount: 0,
    paid_patient_amount: 0,
    patient_amount: 0
  });

  const [editableRowIds, setEditableRowIds] = useState<number[]>([]);

  const handleInputChange = ({
    rowIdx,
    field,
    value
  }: {
    rowIdx: number;
    field: string;
    value: any;
  }) => {
    const updatedRowIndex = cptData.findIndex(
      row => row.rowIdx === rowIdx && !row.deleted
    );
    if (updatedRowIndex < 0) return;

    const updatedRow = {...cptData[updatedRowIndex], [field]: value};

    const updatedCPTCodes = [...cptData];
    updatedCPTCodes[updatedRowIndex] = updatedRow;

    setCPTData(updatedCPTCodes);
  };

  useEffect(() => {
    const cptFormData: Partial<
      IClaimsCPTData | {id: number; deleted: true}
    >[] = cptData.map(cptItem => {
      if (cptItem.deleted) {
        return {
          id: cptItem.id,
          deleted: true
        };
      }
      const cptFieldData: Partial<IClaimsCPTData> = {
        cpt_code: cptItem?.cpt_code,
        charges: cptItem?.charges,
        allowed_amount: cptItem?.allowed_amount,
        insurance_amount: cptItem?.insurance_amount,
        patient_amount: cptItem?.patient_amount,
        paid_insurance_amount: cptItem?.paid_insurance_amount,
        paid_patient_amount: cptItem?.paid_patient_amount,
        balance_amount: cptItem?.balance_amount
      };
      if (cptItem.id) {
        cptFieldData['id'] = cptItem.id;
      }
      return cptFieldData;
    });

    const requiredCPTData = cptFormData.filter(item =>
      Object.values(item).some(v => v)
    );

    methods.setValue('cpt_data', requiredCPTData);
    methods.clearErrors('cpt_data');

    // UPDATE THE AGGREGATE VALUES

    const updatedAggregateValues: IClaimsCPTData & {rowIdx: number} = {
      rowIdx: -1,
      allowed_amount: 0,
      balance_amount: 0,
      charges: 0,
      cpt_code: 0,
      insurance_amount: 0,
      paid_insurance_amount: 0,
      paid_patient_amount: 0,
      patient_amount: 0
    };

    const cptCurrencyFieldKeys = [
      'allowed_amount',
      'balance_amount',
      'charges',
      'insurance_amount',
      'paid_insurance_amount',
      'paid_patient_amount',
      'patient_amount'
    ];
    cptData.forEach(cptItem => {
      cptCurrencyFieldKeys.forEach(fieldKey => {
        if (
          cptItem?.[fieldKey as 'charges'] &&
          typeof cptItem[fieldKey as 'charges'] === 'number'
        ) {
          updatedAggregateValues[fieldKey as 'charges'] += cptItem?.[
            fieldKey as 'charges'
          ] as number;
        }
      });
    });

    setAggregateValues(updatedAggregateValues);
    cptCurrencyFieldKeys.forEach(fieldKey => {
      methods.setValue(fieldKey, updatedAggregateValues[fieldKey as 'charges']);
    });
  }, [cptData]); // eslint-disable-line react-hooks/exhaustive-deps

  const addCPTRow = () => {
    const newRowData = {rowIdx: cptData.length + 1};
    if (cptData.length > 0) {
      let maxRowIdx = cptData[0].rowIdx;
      cptData.forEach(item => {
        maxRowIdx = Math.max(maxRowIdx, item.rowIdx);
      });
      newRowData.rowIdx = maxRowIdx + 1;
    }
    setCPTData([...cptData, newRowData]);
    setEditableRowIds(prevEditableRowId => [
      ...prevEditableRowId,
      newRowData.rowIdx
    ]);
  };

  const onEditRow = (rowIdx: number) => {
    setEditableRowIds(prevEditableRowId => [...prevEditableRowId, rowIdx]);
  };

  const onSaveRow = (rowIdx: number) => {
    setEditableRowIds(prevEditableRowId =>
      prevEditableRowId.filter(i => i != rowIdx)
    );
  };

  const onDeleteRow = (rowIdx: number) => {
    const cptItemArrIdx = cptData.findIndex(c => c.rowIdx === rowIdx);

    if (cptItemArrIdx < 0) return;

    const row = cptData[cptItemArrIdx];

    const updatedCPTCodes = cptData.filter(i => i.rowIdx !== row.rowIdx);

    if (row.id) {
      updatedCPTCodes.push({
        id: row.id,
        deleted: true,
        rowIdx: row.rowIdx
      });
    }
    setCPTData(updatedCPTCodes);
  };

  const renderCurrencyEditCell = ({
    defaultValue,
    onChange,
    placeholder
  }: {
    defaultValue: number | undefined;
    onChange: (value: number | undefined) => void;
    placeholder: string;
  }) => {
    return (
      <FormField
        type="currency"
        defaultValue={defaultValue}
        onChange={onChange}
        style={{minWidth: '5rem', height: '2.8rem'}}
        placeholder={placeholder}
      />
    );
  };

  const renderAmountValue = (amount: string | number = 0) => {
    if (!amount) return '-';
    return (
      <CurrencyFormatter
        value={amount || '0'}
        displayType={'text'}
        thousandSeparator={true}
        prefix={'$'}
        ignoreRound={true}
        useSign={true}
      />
    );
  };

  const commonHeaderStyle = {
    fontFamily: APP.FONT_FAMILY.MEDIUM,
    width: '10%',
    backgroundColor: MESSAGE_GRID_HEADER_BACKGROUND_COLOR,
    color: FONT_PRIMARY_COLOR,
    fontSize: '0.875rem',
    padding: '0.7rem',
    height: '2rem',
    whiteSpace: 'normal',
    border: `1px solid ${EDITABLE_GRID_BORDER_COLOR}`,
    textAlign: 'left'
  };

  const commonCellStyle = {
    fontSize: '0.875rem',
    padding: '0.7rem',
    border: `1px solid ${EDITABLE_GRID_BORDER_COLOR}`
  };

  const filteredGridData = cptData.filter(i => !i.deleted);

  const renderMoreNotesDetails = () => {
    return (
      <CCMShowFullNotesDialog
        open={moreDetails}
        onClose={() => {
          setState((p: any) => ({
            ...p,
            moreDetails: false
          }));
        }}
        notes={cptCode}
        title={t('CLAIMS_TICKETS.CPT_CODES.PROCEDURE_CODE_LABEL')}
      />
    );
  };

  const renderCptCode = (row: any) => {
    const maxCharacters = 30; // Maximum characters to display
    // Check if cpt_code exists and is not null or undefined
    if (!row?.cpt_code) {
      return <p>-</p>; // Return '-' if cpt_code is not available
    }
    return (
      <p>
        {row.cpt_code.length <= maxCharacters ? (
          row.cpt_code // Display full cpt_code if it's within the limit
        ) : (
          <div>
            {row.cpt_code.slice(0, maxCharacters)}
            {'... '}
            <span
              onClick={() => {
                setState((p: any) => ({
                  ...p,
                  moreDetails: true,
                  cptCode: row?.cpt_code
                }));
              }}
              className="link-text-style"
              style={{ cursor: 'pointer' }}>
              {t('CCM.MORE_DETAILS')}
            </span>
          </div>
        )}
      </p>
    );
  };

  return (
    <div className="claims-cpt-grid-wrapper">
      <div className="claims-cpt-grid-header">
        <div className="claims-custom-field-wrapper">
          {renderClaimsFields(methods.getValues())}
        </div>
        <TertiaryButton
          style={{fontWeight: 'bold'}}
          size="small"
          onClick={onClose}>
          {t('COMMON.BACK')}
        </TertiaryButton>
      </div>
      <GridTable
        title={
          <p className="grid-title">
            {t('CLAIMS_TICKETS.CPT_CODES.GRID_TITLE')}
          </p>
        }
        components={{
          Toolbar: toolbarProps => (
            <div className="gird-toolbar-wrapper">
              <MTableToolbar {...toolbarProps} />
              <TertiaryButton style={{fontWeight: 'bold'}} onClick={addCPTRow}>
                {t('COMMON.ADD')}
              </TertiaryButton>
              <Button
                color="primary"
                variant='outlined'
                onClick={resetCPTCodes}
                style={{fontWeight: 'bold'}}>
                {t('COMMON.RESET_BUTTON_LABEL')}
              </Button>
            </div>
          )
        }}
        localization={{
          body: {
            emptyDataSourceMessage: t('CLAIMS_TICKETS.CPT_CODES.NO_CPT_CODES')
          }
        }}
        options={{
          ...DEFAULT_GRID_TABLE__OPTIONS,
          paging: false,
          sorting: false,
          emptyRowsWhenPaging: false,
          showEmptyDataSourceMessage: true,
          maxBodyHeight: '100%',
          // REVERTING DEFAULT OPTION VALUES
          showTitle: true,
          search: false,
          toolbar: true,
          // TODO: WHY DO I NEED IT
          headerStyle: {
            fontFamily: APP.FONT_FAMILY.MEDIUM,
            width: '10%',
            backgroundColor: MESSAGE_GRID_HEADER_BACKGROUND_COLOR,
            fontSize: '0.875rem',
            padding: '0.7rem',
            height: '2rem',
            whiteSpace: 'normal',
            color: FONT_PRIMARY_COLOR
          },
          actionsColumnIndex: -1,
          rowStyle: {
            border: `1px solid ${EDITABLE_GRID_BORDER_COLOR}`
          }
        }}
        style={{
          display: 'flex',
          flexDirection: 'column',
          overflow: 'auto',
          boxShadow:
            '0 0 0 1px rgb(63 63 68 / 5%), 0 1px 3px 0 rgb(63 63 68 / 15%)',
          cursor: 'pointer',
          maxHeight: '100%',
          margin: '0.1rem'
        }}
        columns={[
          {
            title: t('CLAIMS_TICKETS.CPT_CODES.PROCEDURE_CODE_LABEL'),
            field: 'cpt_code',
            editable: 'always',
            headerStyle: {
              ...commonHeaderStyle,
              width: '11%'
            } as CSSProperties,
            cellStyle: {
              ...commonCellStyle,
              width: '11%'
            } as CSSProperties,
            render: (row: IClaimsCPTField) => {
              if (row.rowIdx === -1) {
                return (
                  <p className='total-values-col'>
                    {t('CLAIMS_TICKETS.CPT_CODES.TOTAL_LABEL')}
                  </p>
                );
              }
              if (editableRowIds.includes(row.rowIdx)) {
                return (
                  <FormField
                    type="text"
                    value={row?.cpt_code}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      handleInputChange({
                        field: 'cpt_code',
                        rowIdx: row.rowIdx,
                        value: e.target.value
                      });
                    }}
                    style={{minWidth: '5rem'}}
                    placeholder={t(
                      'CLAIMS_TICKETS.CPT_CODES.PROCEDURE_CODE_PLACEHOLDER'
                    )}
                  />
                );
              }
              return <div>{renderCptCode(row)}</div>
            }
          },
          {
            title: t('CLAIMS_TICKETS.CPT_CODES.CHARGES_LABEL'),
            field: 'charges',
            type: 'numeric',
            headerStyle: {
              ...commonHeaderStyle,
              width: '11%'
            } as CSSProperties,
            cellStyle: {
              ...commonCellStyle,
              width: '11%'
            } as CSSProperties,
            render: (row: IClaimsCPTField) => {
              if (editableRowIds.includes(row.rowIdx)) {
                return renderCurrencyEditCell({
                  defaultValue: row?.charges,
                  onChange: (value: number | undefined) => {
                    handleInputChange({
                      field: 'charges',
                      rowIdx: row.rowIdx,
                      value
                    });
                  },
                  placeholder: t('CLAIMS_TICKETS.CPT_CODES.CHARGES_PLACEHOLDER')
                });
              }
              return <p className={`${row?.rowIdx === -1 ? "total-values-col": ""}`}>{renderAmountValue(row.charges)}</p>;
            }
          },
          {
            title: t('CLAIMS_TICKETS.CPT_CODES.ALLOWED_AMOUNT_LABEL'),
            field: 'allowed_amount',
            type: 'numeric',
            headerStyle: {
              ...commonHeaderStyle,
              width: '11%'
            } as CSSProperties,
            cellStyle: {
              ...commonCellStyle,
              width: '11%'
            } as CSSProperties,
            render: (row: IClaimsCPTField) => {
              if (editableRowIds.includes(row?.rowIdx)) {
                return renderCurrencyEditCell({
                  defaultValue: row?.allowed_amount,
                  onChange: value => {
                    handleInputChange({
                      field: 'allowed_amount',
                      rowIdx: row.rowIdx,
                      value
                    });
                  },
                  placeholder: t(
                    'CLAIMS_TICKETS.CPT_CODES.ALLOWED_AMOUNT_PLACEHOLDER'
                  )
                });
              }
              return <p className={`${row?.rowIdx === -1 ? "total-values-col": ""}`}>{renderAmountValue(row.allowed_amount)}</p>;
            }
          },
          {
            title: t('CLAIMS_TICKETS.CPT_CODES.INSURANCE_AMOUNT_LABEL'),
            field: 'insurance_amount',
            type: 'numeric',
            headerStyle: {
              ...commonHeaderStyle,
              width: '11%'
            } as CSSProperties,
            cellStyle: {
              ...commonCellStyle,
              width: '11%'
            } as CSSProperties,
            render: (row: IClaimsCPTField) => {
              if (editableRowIds.includes(row?.rowIdx)) {
                return renderCurrencyEditCell({
                  defaultValue: row?.insurance_amount,
                  onChange: value => {
                    handleInputChange({
                      field: 'insurance_amount',
                      rowIdx: row.rowIdx,
                      value
                    });
                  },
                  placeholder: t(
                    'CLAIMS_TICKETS.CPT_CODES.INSURANCE_AMOUNT_PLACEHOLDER'
                  )
                });
              }
              return <p className={`${row?.rowIdx === -1 ? "total-values-col": ""}`}>{renderAmountValue(row.insurance_amount)}</p>;
            }
          },
          {
            title: t('CLAIMS_TICKETS.CPT_CODES.PATIENT_AMOUNT_LABEL'),
            field: 'patient_amount',
            type: 'numeric',
            headerStyle: {
              ...commonHeaderStyle,
              width: '11%'
            } as CSSProperties,
            cellStyle: {
              ...commonCellStyle,
              width: '11%'
            } as CSSProperties,
            render: (row: IClaimsCPTField) => {
              if (editableRowIds.includes(row?.rowIdx)) {
                return renderCurrencyEditCell({
                  defaultValue: row?.patient_amount,
                  onChange: value => {
                    handleInputChange({
                      field: 'patient_amount',
                      rowIdx: row.rowIdx,
                      value
                    });
                  },
                  placeholder: t(
                    'CLAIMS_TICKETS.CPT_CODES.PATIENT_AMOUNT_PLACEHOLDER'
                  )
                });
              }
              return <p className={`${row?.rowIdx === -1 ? "total-values-col": ""}`}>{renderAmountValue(row.patient_amount)}</p>;
            }
          },
          {
            title: t('CLAIMS_TICKETS.CPT_CODES.PAID_INSURANCE_AMOUNT_LABEL'),
            field: 'paid_insurance_amount',
            type: 'numeric',
            headerStyle: {
              ...commonHeaderStyle,
              width: '11%'
            } as CSSProperties,
            cellStyle: {
              ...commonCellStyle,
              width: '11%'
            } as CSSProperties,
            render: (row: IClaimsCPTField) => {
              if (editableRowIds.includes(row?.rowIdx)) {
                return renderCurrencyEditCell({
                  defaultValue: row?.paid_insurance_amount,
                  onChange: value => {
                    handleInputChange({
                      field: 'paid_insurance_amount',
                      rowIdx: row.rowIdx,
                      value
                    });
                  },
                  placeholder: t(
                    'CLAIMS_TICKETS.CPT_CODES.PAID_INSURANCE_AMOUNT_PLACEHOLDER'
                  )
                });
              }
              return <p className={`${row?.rowIdx === -1 ? "total-values-col": ""}`}>{renderAmountValue(row.paid_insurance_amount)}</p>;
            }
          },
          {
            title: t('CLAIMS_TICKETS.CPT_CODES.PAID_PATIENT_AMOUNT_LABEL'),
            field: 'paid_patient_amount',
            type: 'numeric',
            headerStyle: {
              ...commonHeaderStyle,
              width: '11%'
            } as CSSProperties,
            cellStyle: {
              ...commonCellStyle,
              width: '11%'
            } as CSSProperties,
            render: (row: IClaimsCPTField) => {
              if (editableRowIds.includes(row?.rowIdx)) {
                return renderCurrencyEditCell({
                  defaultValue: row?.paid_patient_amount,
                  onChange: value => {
                    handleInputChange({
                      field: 'paid_patient_amount',
                      rowIdx: row.rowIdx,
                      value
                    });
                  },
                  placeholder: t(
                    'CLAIMS_TICKETS.CPT_CODES.PAID_PATIENT_AMOUNT_PLACEHOLDER'
                  )
                });
              }
              return <p className={`${row?.rowIdx === -1 ? "total-values-col": ""}`}>{renderAmountValue(row.paid_patient_amount)}</p>;
            }
          },
          {
            title: t('CLAIMS_TICKETS.CPT_CODES.BALANCE_AMOUNT'),
            field: 'balance_amount',
            type: 'numeric',
            headerStyle: {
              ...commonHeaderStyle,
              width: '11%'
            } as CSSProperties,
            cellStyle: {
              ...commonCellStyle,
              width: '11%'
            } as CSSProperties,
            render: (row: IClaimsCPTField) => {
              if (editableRowIds.includes(row?.rowIdx)) {
                return renderCurrencyEditCell({
                  defaultValue: row?.balance_amount,
                  onChange: value => {
                    handleInputChange({
                      field: 'balance_amount',
                      rowIdx: row.rowIdx,
                      value
                    });
                  },
                  placeholder: t(
                    'CLAIMS_TICKETS.CPT_CODES.BALANCE_AMOUNT_PLACEHOLDER'
                  )
                });
              }
              return <p className={`${row?.rowIdx === -1 ? "total-values-col": ""}`}>{renderAmountValue(row.balance_amount)}</p>;
            }
          },
          {
            title: t('CLAIMS_TICKETS.CPT_CODES.ACTION_HEADER_LABEL'),
            field: 'actions',
            headerStyle: {
              ...commonHeaderStyle,
              width: '11%'
            } as CSSProperties,
            cellStyle: {
              ...commonCellStyle,
              width: '11%'
            } as CSSProperties,
            render: (row: IClaimsCPTField) => {
              if (row.rowIdx === -1) return null;
              return (
                <div>
                  {editableRowIds.includes(row?.rowIdx) ? (
                    <Tooltip title={t('COMMON.SAVE') as string}>
                      <IconButton
                        onClick={() => onSaveRow(row?.rowIdx)}
                        className="action-icon-button">
                        <CheckIcon />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <>
                      <Tooltip title={t('COMMON.EDIT') as string}>
                        <IconButton
                          onClick={() => onEditRow(row?.rowIdx)}
                          className="action-icon-button">
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip
                        title={t('COMMON.DELETE') as string}
                        className="action-icon-button">
                        <IconButton onClick={() => onDeleteRow(row?.rowIdx)}>
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </div>
              );
            }
          }
        ]}
        data={[
          ...filteredGridData,
          ...(filteredGridData.length > 0 ? [aggregateValues] : [])
        ]}
      />
      {moreDetails && renderMoreNotesDetails()}
    </div>
  );
};

export default ClaimsCPTFields;
